import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouterState } from "@tanstack/react-router";

export type BannerKind = "success" | "info" | "warning" | "error";

export type BannerAction = {
  label: string;
  onClick: () => void;
  testId?: string;
};

export type BannerDescriptor = {
  id?: string;
  kind: BannerKind;
  body: ReactNode;
  action?: BannerAction;
  autoDismissAfterMs?: number | null;
  dismissAfterRouteChange?: boolean;
  testId?: string;
};

type StoredBanner = {
  id: string;
  kind: BannerKind;
  body: ReactNode;
  action: BannerAction | undefined;
  autoDismissAfterMs: number | null;
  dismissAfterRouteChange: boolean;
  testId: string;
  shownAtPath: string;
};

type BannerQueueContextValue = {
  banners: ReadonlyArray<StoredBanner>;
  show: (banner: BannerDescriptor) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
};

const BannerQueueContext = createContext<BannerQueueContextValue | null>(null);

// M10-U3 Q213: success/info are ephemeral and auto-dismiss; warning/error are
// sticky alarms requiring acknowledgement. Per-banner overrides allowed.
// M10-U3 i1 (Q215 inferred): success/info dismiss on route change (V2-3-U5
// posture); warning/error survive route changes consistent with the sticky
// contract.
const KIND_DEFAULTS: Record<
  BannerKind,
  { autoDismissAfterMs: number | null; dismissAfterRouteChange: boolean }
> = {
  success: { autoDismissAfterMs: 5000, dismissAfterRouteChange: true },
  info: { autoDismissAfterMs: 5000, dismissAfterRouteChange: true },
  warning: { autoDismissAfterMs: null, dismissAfterRouteChange: false },
  error: { autoDismissAfterMs: null, dismissAfterRouteChange: false },
};

let idCounter = 0;
function nextBannerId(): string {
  idCounter += 1;
  return `banner-${idCounter}`;
}

export function BannerQueueProvider({ children }: { children: ReactNode }) {
  const [banners, setBanners] = useState<StoredBanner[]>([]);
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const currentPathRef = useRef(currentPath);
  const timeoutsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);

  const dismiss = useCallback((id: string) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
    const handle = timeoutsRef.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const dismissAll = useCallback(() => {
    setBanners([]);
    for (const handle of timeoutsRef.current.values()) {
      window.clearTimeout(handle);
    }
    timeoutsRef.current.clear();
  }, []);

  const show = useCallback(
    (descriptor: BannerDescriptor): string => {
      const defaults = KIND_DEFAULTS[descriptor.kind];
      const id = descriptor.id ?? nextBannerId();
      const autoDismissAfterMs =
        descriptor.autoDismissAfterMs === undefined
          ? defaults.autoDismissAfterMs
          : descriptor.autoDismissAfterMs;
      const dismissAfterRouteChange =
        descriptor.dismissAfterRouteChange === undefined
          ? defaults.dismissAfterRouteChange
          : descriptor.dismissAfterRouteChange;
      const testId = descriptor.testId ?? `banner-${descriptor.kind}`;
      const stored: StoredBanner = {
        id,
        kind: descriptor.kind,
        body: descriptor.body,
        action: descriptor.action,
        autoDismissAfterMs,
        dismissAfterRouteChange,
        testId,
        shownAtPath: currentPathRef.current,
      };
      // FIFO: oldest renders first; new banners append. If id collides (same
      // id reused), replace the existing entry in place to keep id-based
      // dismissal idempotent.
      setBanners((prev) => {
        const existingIdx = prev.findIndex((b) => b.id === id);
        if (existingIdx !== -1) {
          const next = prev.slice();
          next[existingIdx] = stored;
          const oldHandle = timeoutsRef.current.get(id);
          if (oldHandle !== undefined) {
            window.clearTimeout(oldHandle);
            timeoutsRef.current.delete(id);
          }
          return next;
        }
        return [...prev, stored];
      });
      if (autoDismissAfterMs !== null) {
        const handle = window.setTimeout(() => {
          dismiss(id);
        }, autoDismissAfterMs);
        timeoutsRef.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    setBanners((prev) => {
      const survivors = prev.filter(
        (b) => !(b.dismissAfterRouteChange && b.shownAtPath !== currentPath),
      );
      if (survivors.length === prev.length) {
        return prev;
      }
      const removed = prev.filter((b) => !survivors.includes(b));
      for (const b of removed) {
        const handle = timeoutsRef.current.get(b.id);
        if (handle !== undefined) {
          window.clearTimeout(handle);
          timeoutsRef.current.delete(b.id);
        }
      }
      return survivors;
    });
  }, [currentPath]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      for (const handle of timeouts.values()) {
        window.clearTimeout(handle);
      }
      timeouts.clear();
    };
  }, []);

  const value = useMemo<BannerQueueContextValue>(
    () => ({ banners, show, dismiss, dismissAll }),
    [banners, show, dismiss, dismissAll],
  );

  return (
    <BannerQueueContext.Provider value={value}>
      {children}
    </BannerQueueContext.Provider>
  );
}

export function useBannerQueue(): BannerQueueContextValue {
  const ctx = useContext(BannerQueueContext);
  if (ctx === null) {
    throw new Error("useBannerQueue must be used inside a BannerQueueProvider");
  }
  return ctx;
}

const KIND_CONTAINER_CLASSES: Record<BannerKind, string> = {
  success: "border-outline bg-success/10 text-on-surface",
  info: "border-outline bg-info/10 text-on-surface",
  warning: "border-outline bg-warning/10 text-on-surface",
  error: "border-outline bg-error/10 text-on-surface",
};

export function BannerStack() {
  const { banners, dismiss } = useBannerQueue();
  if (banners.length === 0) {
    return null;
  }
  return (
    <div data-testid="banner-stack">
      {banners.map((b) => (
        <div
          key={b.id}
          role="status"
          aria-live="polite"
          data-testid={b.testId}
          className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${KIND_CONTAINER_CLASSES[b.kind]}`}
        >
          <span className="flex-1">{b.body}</span>
          {b.action !== undefined ? (
            <button
              type="button"
              onClick={() => {
                b.action?.onClick();
                dismiss(b.id);
              }}
              className="rounded-control px-2 py-1 font-medium text-primary underline-offset-2 hover:underline"
              data-testid={b.action.testId ?? `${b.testId}-action`}
            >
              {b.action.label}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => dismiss(b.id)}
            aria-label="Dismiss banner"
            className="rounded-control px-2 py-1 text-on-surface-variant hover:bg-surface-container"
            data-testid={`${b.testId}-dismiss`}
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
